import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Gem, Search, Heart, CheckCircle } from 'lucide-react';
import SearchBar from '../components/SearchBar';
import CitySelector from '../components/CitySelector';
import RotatingText from '../components/RotatingText';
import Card from '../components/Card';
import useEscortsData from '../hooks/useEscortsData';
import { createEscortUrl } from '../utils/urlUtils';
import { useCountryCode } from '../hooks/useCountryCode';
import { useTranslation } from '../hooks/useTranslation';
import { useSearch } from '../contexts/SearchContext';
import useMobile from '../hooks/useMobile';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { countryCode } = useCountryCode();
  const { t } = useTranslation();
  const { isMobileSearchVisible, hideMobileSearch } = useSearch();
  const isMobile = useMobile();
  const {
    escorts, 
    loading, 
    refetch
  } = useEscortsData();

  const filteredEscorts = React.useMemo(() => {
    if (!escorts || !countryCode) return escorts || [];
    return escorts.filter(escort => !escort.blocked?.includes(countryCode));
  }, [escorts, countryCode]);

  React.useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chiude la ricerca mobile quando si clicca fuori dall'header
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && isMobileSearchVisible) {
        // Non chiude se il click è nell'header o sui suoi elementi
        const target = event.target as Element;
        if (!target.closest('header')) {
          hideMobileSearch();
        }
      }
    };

    // Aggiunge il listener per i click su tutta la pagina
    if (isMobile && isMobileSearchVisible) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMobile, isMobileSearchVisible, hideMobileSearch]);

  const handleCitySelect = (location: string) => {
    navigate(`/location/${encodeURIComponent(location)}`);
  };

  const handleSearch = (query: string, location: string) => {
    // Nasconde la ricerca mobile dopo aver effettuato la ricerca
    if (isMobile && isMobileSearchVisible) {
      hideMobileSearch();
    }
    navigate(`/escorts?search=${query}&location=${location}`);
  };
  
  const handleViewProfile = (id: string) => {
    const escort = filteredEscorts.find(e => e.id === id);
    if (escort && escort.phone) {
      const url = createEscortUrl({
        phone: escort.phone,
        name: escort.name,
        location: escort.location
      });
      navigate(url);
    }
  };
  
  // Funzione per randomizzare array
  function shuffleArray<T>(array: T[]): T[] {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  // Stato per mostrare/nascondere tutte le righe
  const [showAll, setShowAll] = React.useState(false);
  const [showAllRegular, setShowAllRegular] = React.useState(false);
  const [showAllVip, setShowAllVip] = React.useState(false);

  const vipRandom = React.useMemo(() => {
    return shuffleArray(filteredEscorts.filter(e => e.featured));
  }, [filteredEscorts]);

  const vetrinaRandom = React.useMemo(() => {
    return shuffleArray(filteredEscorts.filter(e => !e.featured && e.isVetrina));
  }, [filteredEscorts]);
  
  const regularRandom = React.useMemo(() => {
    return shuffleArray(filteredEscorts.filter(e => !e.featured && !e.isVetrina));
  }, [filteredEscorts]);

  const rotatingWords = [
    t('common.categories.compagna'),
    t('common.categories.escort'),
    t('common.trans'),
    // t('common.categories.accompagnatrice'),
    t('common.categories.mistress'),
  ];

  return (
    <div>
      <section className={`relative bg-gradient-to-r from-pink-500 to-purple-600 text-white transition-all duration-300 ${isMobile && !isMobileSearchVisible ? 'hidden' : ''}`}>
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div className="relative container mx-auto px-4 py-8 md:py-24 flex flex-col items-center text-center">

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {t('homePage.heroTitle').replace('{type}', '')} <RotatingText words={rotatingWords} className="text-white" /> {t('common.rotating.perfetta')}
            </h1>
            <p className="text-xl md:text-2xl mb-12 max-w-3xl">
              {t('homePage.heroSubtitle').replace('{type}', '')} <RotatingText words={rotatingWords} className="font-medium" /> {t('common.rotating.paese')}
            </p>
            
            <div className="w-full max-w-4xl">
              <SearchBar onSearch={handleSearch} />
            </div>

        </div>
      </section>

      { !isMobile && <CitySelector onCitySelect={handleCitySelect} /> }

      {/* Indicatore di caricamento */}
      {loading && (
        <section className="py-8">
          <div className="container mx-auto px-4 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto"></div>
            <p className="mt-2 text-gray-600">{t('homePage.loadingAds')}</p>
          </div>
        </section>
      )}

      {/* Sezione Annunci VIP */}
      {vipRandom.length > 0 && (
        <section className="py-12 bg-gradient-to-r from-amber-50 to-yellow-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center">
                <Crown className="h-8 w-8 text-amber-500 mr-3" />
                <h2 className="text-3xl font-bold text-gray-800">{t('homePage.vipAdsTitle')}</h2>
              </div>
              <p className="text-gray-600">{t('homePage.vipAdsCount', { count: vipRandom.length })}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {(showAllVip ? vipRandom : vipRandom.slice(0, 8)).map((escort) => (
                <Card 
                  key={escort.id}
                  escort={escort} 
                  onViewProfile={handleViewProfile}
                />
              ))}
            </div>
            {vipRandom.length > 8 && (
              <div className="flex justify-center mt-6">
                <button
                  className="px-6 py-2 rounded-full bg-amber-400 hover:bg-amber-500 text-white font-semibold shadow-md transition-colors"
                  onClick={() => setShowAllVip((prev) => !prev)}
                  aria-label={showAllVip ? t('homePage.hideButton') : t('homePage.showAllButton')}
                >
                  {showAllVip ? t('homePage.hideButton') : t('homePage.showAllButton')}
                </button>
              </div>
            )}
          </div>
        </section>
      )}
      
      {/* Sezione Annunci Vetrina */}
      {vetrinaRandom.length > 0 && (
        <section className="py-12 bg-gradient-to-r from-yellow-50 to-orange-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center">
                <Gem className="h-8 w-8 text-yellow-500 mr-3" />
                <h2 className="text-3xl font-bold text-gray-800">{t('homePage.showcaseAdsTitle')}</h2>
              </div>
              <p className="text-gray-600">{t('homePage.showcaseAdsCount', { count: vetrinaRandom.length })}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {(showAll ? vetrinaRandom : vetrinaRandom.slice(0, 8)).map((escort) => (
                <Card 
                  key={escort.id}
                  escort={escort} 
                  onViewProfile={handleViewProfile}
                />
              ))}
            </div>
            {vetrinaRandom.length > 8 && (
              <div className="flex justify-center mt-6">
                <button
                  className="px-6 py-2 rounded-full bg-yellow-400 hover:bg-yellow-500 text-white font-semibold shadow-md transition-colors"
                  onClick={() => setShowAll((prev) => !prev)}
                  aria-label={showAll ? t('homePage.hideButton') : t('homePage.showAllButton')}
                >
                  {showAll ? t('homePage.hideButton') : t('homePage.showAllButton')}
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Sezione Annunci Normali */}
      {regularRandom.length > 0 && (
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-800">{t('homePage.regularAdsTitle')}</h2>
              <p className="text-gray-600">{t('homePage.regularAdsCount', { count: regularRandom.length })}</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {(showAllRegular ? regularRandom : regularRandom.slice(0, 8)).map((escort) => (
                <Card 
                  key={escort.id}
                  escort={escort} 
                  onViewProfile={handleViewProfile}
                />
              ))}
            </div>

            {regularRandom.length > 8 && (
              <div className="flex justify-center mt-6">
                <button
                  className="px-6 py-2 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-semibold shadow-md transition-colors"
                  onClick={() => setShowAllRegular((prev) => !prev)}
                  aria-label={showAllRegular ? t('homePage.hideButton') : t('homePage.showAllButton')}
                >
                  {showAllRegular ? t('homePage.hideButton') : t('homePage.showAllButton')}
                </button>
              </div>
            )}
          </div>
        </section>
      )}
      
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">{t('homePage.howItWorksTitle')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="h-8 w-8 text-pink-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('homePage.searchStepTitle')}</h3>
              <p className="text-gray-600">
                {t('homePage.searchStepDescription')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-pink-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('homePage.contactStepTitle')}</h3>
              <p className="text-gray-600">
                {t('homePage.contactStepDescription')}
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-pink-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('homePage.enjoyStepTitle')}</h3>
              <p className="text-gray-600">
                {t('homePage.enjoyStepDescription')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">{t('common.perch_scegliere_escortfun')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('common.profili_verificati')}</h3>
              <p className="text-gray-600">
                {t('homePage.verifiedProfilesDescription')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-blue-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('homePage.privateSecureTitle')}</h3>
              <p className="text-gray-600">
                {t('homePage.privateSecureDescription')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="h-8 w-8 text-purple-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('homePage.wideSelectionTitle')}</h3>
              <p className="text-gray-600">
                {t('homePage.wideSelectionDescription')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;

